/**
 * The quiet "prefer in person?" line under the online-visit card on a doctor page with no booking.
 *
 * Owner decisions (ghane WA §16.9.1, 2026-09-18): online visit is our preferred option and stays the main
 * action, but in-person is never ruled out — it is offered as one lower-weight line. The line only
 * counts doctors that show a real first free slot («اولین نوبت») on the in-person search for the
 * doctor's own clinic city; a "نوبت‌دهی" button without a slot is not a free slot (Bojnurd ophthalmology:
 * 10 local doctors, 1 with a slot, 2026-09-18).
 */

type Action = { title?: string; top_title?: string };
type Center = { id?: string | number; city_name?: string; city_en_slug?: string };
type Hit = { title?: string; actions?: Action[]; centers?: Center[] };

const CONSULT_CENTER_ID = '5532';
const stripHtml = (s: string) => s.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
const normalize = (s?: string | null) => (s ?? '').replace(/[‌\s]/g, '');

/** First free in-person slot text of a hit («فردا 4 عصر»), or null when it has none. */
export const inPersonSlot = (hit: Hit): string | null => {
  for (const action of hit.actions ?? []) {
    if (!normalize(action.title).startsWith('نوبت') || !action.top_title) continue;
    const text = stripHtml(action.top_title);
    if (text.startsWith('اولین نوبت')) return text.replace(/^اولین نوبت\s*:\s*/, '');
  }
  return null;
};

export type InPersonLine = { count: number; earliest: string; cityName: string | null };

/**
 * Summarises the in-person search: how many hits have a real slot, the soonest one («امروز» beats
 * «فردا» beats any date), and the city name shown to the patient. The doctor whose page this is never
 * counts. Returns null when no hit has a slot — then the line is not shown at all.
 */
export const buildInPersonLine = (hits: Hit[] | undefined, currentDisplayName?: string): InPersonLine | null => {
  const withSlot = (hits ?? [])
    .filter(hit => normalize(hit.title) !== normalize(currentDisplayName))
    .map(hit => ({ hit, slot: inPersonSlot(hit) }))
    .filter((x): x is { hit: Hit; slot: string } => !!x.slot);
  if (withSlot.length === 0) return null;
  const slots = withSlot.map(x => x.slot);
  const earliest = slots.find(s => s.startsWith('امروز')) ?? slots.find(s => s.startsWith('فردا')) ?? slots[0];
  const cityName =
    withSlot
      .flatMap(x => x.hit.centers ?? [])
      .find(center => String(center.id) !== CONSULT_CENTER_ID && center.city_name)?.city_name ?? null;
  return { count: withSlot.length, earliest, cityName };
};

/** City for the IN-PERSON line only: the doctor's own city if set, else the first clinic's city.
 *  Never feed this to the legacy online-visit substitute search — that one must keep its
 *  nationwide ('ir') fallback for a doctor with no city (regression found 2026-09-20). */
export const substituteCity = (doctorCity?: string | null, centers?: Center[] | null): string =>
  doctorCity ||
  (centers ?? []).find(center => String(center.id) !== CONSULT_CENTER_ID && center.city_en_slug)?.city_en_slug ||
  '';

/** The in-person line's city, with the profile's own city as the fallback. Kept pure so the
 *  wiring invariant ("only the in-person line narrows to a clinic city") is testable. */
export const resolveInPersonCity = (inPersonCity?: string | null, doctorCity?: string | null): string =>
  inPersonCity || doctorCity || '';
